import { StringKey } from '../../../../lang/index';

export enum CapTableTab {
  BY_SHARE_CLASS = 'by-share-class',
  BY_STAKEHOLDER = 'by-stakeholder',
}

export const CapTableTabTitle: Record<CapTableTab, StringKey> = {
  [CapTableTab.BY_STAKEHOLDER]: 'BY_STAKEHOLDER',
  [CapTableTab.BY_SHARE_CLASS]: 'BY_SHARE_CLASS',
};

export enum StakeholdersViewBy {
  STAKEHOLDERS = 'stakeholders',
  TYPE = 'type',
}

export enum Column {
  AVERAGE_SHARE_PRICE = 'average-share-price',
  COMMON_STOCK = 'common-stock',
  CONVERTED_PERCENTAGE = 'converted-percentage',
  CONVERTED_SHARES = 'converted-shares',
  DILUTED = 'diluted',
  DILUTED_SHARES = 'diluted-shares',
  EMPTY = 'empty',
  INVESTMENT = 'investment',
  ISSUED = 'issued',
  NAME = 'name',
  NO_ISSUED = 'no-issued',
  NO_OF_STAKEHOLDERS = 'no-of-stakeholders',
  OTHER_SHARE_CLASSES = 'other-share-classes',
  STAKEHOLDER_NAME = 'stakeholder-name',
  TYPE = 'type',
  VOTING = 'voting',
  VOTING_RIGHTS = 'voting-rights',
}

export const columnTitle: Record<Column, StringKey> = {
  [Column.NAME]: 'NAME',
  [Column.STAKEHOLDER_NAME]: 'STAKEHOLDER_NAME',
  [Column.TYPE]: 'TYPE',
  [Column.ISSUED]: 'ISSUED_PERCENTAGE',
  [Column.DILUTED]: 'DILUTED_PERCENTAGE',
  [Column.VOTING]: 'VOTING_PERCENTAGE',
  [Column.NO_ISSUED]: 'NO_ISSUED',
  [Column.DILUTED_SHARES]: 'DILUTED_SHARES',
  [Column.COMMON_STOCK]: 'COMMON_STOCK',
  [Column.OTHER_SHARE_CLASSES]: 'OTHER_SHARE_CLASSES',
  [Column.INVESTMENT]: 'INVESTMENT',
  [Column.AVERAGE_SHARE_PRICE]: 'AVERAGE_SHARE_PRICE',
  [Column.VOTING_RIGHTS]: 'VOTING_RIGHTS',
  [Column.EMPTY]: 'EMPTY',
  [Column.CONVERTED_SHARES]: 'CONVERTED_BASIS',
  [Column.NO_OF_STAKEHOLDERS]: 'NO_OF_STAKEHOLDERS',
  [Column.CONVERTED_PERCENTAGE]: 'CONVERTED_BASIS_PERCENT',
};

export type StakeholderColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.COMMON_STOCK
  | Column.CONVERTED_PERCENTAGE
  | Column.CONVERTED_SHARES
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.INVESTMENT
  | Column.ISSUED
  | Column.NAME
  | Column.NO_ISSUED
  | Column.OTHER_SHARE_CLASSES
  | Column.STAKEHOLDER_NAME
  | Column.TYPE
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export type ShareClassColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.CONVERTED_PERCENTAGE
  | Column.CONVERTED_SHARES
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.INVESTMENT
  | Column.ISSUED
  | Column.NAME
  | Column.NO_ISSUED
  | Column.NO_OF_STAKEHOLDERS
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export type ColumnVariant = ShareClassColumns | StakeholderColumns;

export const customizeColumns: Record<CapTableTab, ColumnVariant[]> = {
  [CapTableTab.BY_STAKEHOLDER]: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
  [CapTableTab.BY_SHARE_CLASS]: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.INVESTMENT,
    Column.VOTING_RIGHTS,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
    Column.NO_OF_STAKEHOLDERS,
  ],
};

export type CustomView = Record<
  CapTableTab,
  {
    columns: Column[];
    viewBy?: Record<StakeholdersViewBy, boolean>;
  }
>;

const customViewFieldList = Object.values(Column);
export const capTableTabTitleTitleMap = Object.entries(CapTableTabTitle);

export const defaultCustomView = {
  [CapTableTab.BY_SHARE_CLASS]: {
    columns: customViewFieldList,
  },
  [CapTableTab.BY_STAKEHOLDER]: {
    columns: customViewFieldList,
    viewBy: {
      [StakeholdersViewBy.STAKEHOLDERS]: true,
      [StakeholdersViewBy.TYPE]: true,
    },
  },
};

export const stakeholdersViewByColumns: Record<'all' | StakeholdersViewBy, ColumnVariant[]> = {
  all: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
  [StakeholdersViewBy.STAKEHOLDERS]: [
    Column.STAKEHOLDER_NAME,
    Column.TYPE,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
  [StakeholdersViewBy.TYPE]: [
    Column.TYPE,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.CONVERTED_SHARES,
    Column.CONVERTED_PERCENTAGE,
  ],
};

export const shareClassesViewByColumns: ColumnVariant[] =
  customizeColumns[CapTableTab.BY_SHARE_CLASS];

export type CapTableRowValue = { change: number; value: number };

export type ScenarioFundraisingCapTableRow = {
  avgSharePrice: CapTableRowValue;
  convertedBasis: CapTableRowValue;
  convertedBasisPercentage: CapTableRowValue;
  defaultShareClass: { id: string; name: string } & CapTableRowValue;
  diluted: CapTableRowValue;
  dilutedPercentage: CapTableRowValue;
  investment: CapTableRowValue;
  issued: CapTableRowValue;
  issuedPercentage: CapTableRowValue;
  shareClasses: ({ id: string; name: string } & CapTableRowValue)[];
  unvested?: CapTableRowValue;
  unvestedPercentage?: CapTableRowValue;
  votingPercentage: CapTableRowValue;
  votingRights: CapTableRowValue;
};
